import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import LabForm from "../components/LabForm";
import LabCharts from "../components/LabCharts";
import LabComparison from "../components/LabComparison";
import StageDisplay from "../components/StageDisplay";
import ManagementPlan from "../components/ManagementPlan";
import RecheckReminder from "../components/RecheckReminder";
import EditCatDialog from "../components/EditCatDialog";
import EditLabDialog from "../components/EditLabDialog";
import { api, formatApiError } from "../lib/api";
import { computeIRIS } from "../lib/iris";
import { exportLabsCSV, exportLabsPDF } from "../lib/export";
import { useUnits, creatinineDisplay, creatinineUnitLabel, weightDisplay, weightUnitLabel } from "../context/UnitsContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { ChevronLeft, Trash2, PawPrint, Pencil, Download, FileText, FileSpreadsheet } from "lucide-react";

export default function CatDetail() {
  const { catId } = useParams();
  const [cat, setCat] = useState(null);
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCatOpen, setEditCatOpen] = useState(false);
  const [editingLab, setEditingLab] = useState(null);
  const { units } = useUnits();
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [c, l] = await Promise.all([api.get(`/cats/${catId}`), api.get(`/cats/${catId}/labs`)]);
      setCat(c.data);
      setLabs(l.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [catId]);

  const iris = useMemo(() => computeIRIS(labs), [labs]);

  const deleteLab = async (id) => {
    try {
      await api.delete(`/cats/${catId}/labs/${id}`);
      toast.success("Lab deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteCat = async () => {
    try {
      await api.delete(`/cats/${catId}`);
      toast.success("Cat removed");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-muted-foreground">Loading…</div>
      </Layout>
    );
  }

  if (!cat) {
    return (
      <Layout>
        <div className="text-muted-foreground">Cat not found.</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition"
        data-testid="back-to-dashboard-link"
      >
        <ChevronLeft size={16} /> Back to your cats
      </Link>

      <div className="mt-4 flex items-start justify-between gap-6 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl overflow-hidden bg-accent/15 text-accent flex items-center justify-center">
            {cat.photo ? (
              <img src={cat.photo} alt={cat.name} className="w-full h-full object-cover" data-testid="cat-photo" />
            ) : (
              <PawPrint size={26} />
            )}
          </div>
          <div>
            <h1 className="font-display text-4xl tracking-tight" data-testid="cat-name-header">
              {cat.name}
            </h1>
            <div className="mt-1 text-sm text-muted-foreground">
              {[cat.breed, cat.sex, cat.date_of_birth, cat.weight_kg != null ? `${weightDisplay(cat.weight_kg, units)} ${weightUnitLabel(units)}` : null]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <LabForm catId={catId} onAdded={load} />
          <Button
            variant="outline"
            onClick={() => setEditCatOpen(true)}
            data-testid="edit-cat-button"
            className="rounded-full"
          >
            <Pencil size={16} className="mr-1.5" /> Edit
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-menu-button" className="rounded-full">
                <Download size={16} className="mr-1.5" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => exportLabsCSV(cat, labs, units)}
                data-testid="export-csv-item"
              >
                <FileSpreadsheet size={14} className="mr-2" /> Download CSV
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => exportLabsPDF(cat, labs, units)}
                data-testid="export-pdf-item"
              >
                <FileText size={14} className="mr-2" /> Download PDF report
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" data-testid="delete-cat-button" className="rounded-full">
                <Trash2 size={16} className="mr-1.5" /> Remove
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="delete-cat-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Remove {cat.name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will delete this cat and all associated lab results. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-cat-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={deleteCat} data-testid="delete-cat-confirm">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="mt-6">
        <RecheckReminder iris={iris} labs={labs} />
      </div>

      <EditCatDialog
        cat={cat}
        open={editCatOpen}
        onOpenChange={setEditCatOpen}
        onSaved={(updated) => setCat(updated)}
      />
      {editingLab && (
        <EditLabDialog
          catId={catId}
          lab={editingLab}
          open={!!editingLab}
          onOpenChange={(o) => !o && setEditingLab(null)}
          onSaved={load}
        />
      )}

      <div className="mt-8">
        <StageDisplay iris={iris} />
      </div>

      {iris?.ready && (
        <div className="mt-10">
          <ManagementPlan iris={iris} />
        </div>
      )}

      <div className="mt-10">
        <h3 className="font-display text-2xl">Trends</h3>
        <p className="text-sm text-muted-foreground mt-1 mb-4">
          Values plotted in the order they were recorded. Dashed lines mark IRIS thresholds.
        </p>
        {labs.length === 0 ? (
          <div className="card-soft p-10 text-center text-muted-foreground text-sm" data-testid="no-labs-empty">
            No lab results yet. Add your first result to start trending.
          </div>
        ) : (
          <LabCharts labs={labs} />
        )}
      </div>

      <LabComparison labs={labs} />

      <div className="mt-10">
        <h3 className="font-display text-2xl">Lab history</h3>
        <div className="card-soft mt-4 overflow-hidden">
          <Table data-testid="labs-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Creatinine ({creatinineUnitLabel(units)})</TableHead>
                <TableHead>SDMA (µg/dL)</TableHead>
                <TableHead>USG</TableHead>
                <TableHead>UPC</TableHead>
                <TableHead>SBP</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {labs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-muted-foreground text-sm py-8 text-center">
                    No labs recorded yet.
                  </TableCell>
                </TableRow>
              ) : (
                [...labs]
                  .sort((a, b) => b.date.localeCompare(a.date))
                  .map((l) => (
                    <TableRow key={l.id} data-testid={`lab-row-${l.id}`}>
                      <TableCell className="font-medium">{l.date}</TableCell>
                      <TableCell>{creatinineDisplay(l.creatinine_mg_dl, units) || "—"}</TableCell>
                      <TableCell>{l.sdma_ug_dl ?? "—"}</TableCell>
                      <TableCell>{l.usg ?? "—"}</TableCell>
                      <TableCell>{l.upc ?? "—"}</TableCell>
                      <TableCell>{l.bp_systolic ?? "—"}</TableCell>
                      <TableCell className="text-muted-foreground text-xs max-w-[220px] truncate">
                        {l.notes || ""}
                      </TableCell>
                      <TableCell className="flex gap-1 justify-end">
                        <Button
                          onClick={() => setEditingLab(l)}
                          variant="ghost"
                          size="icon"
                          data-testid={`edit-lab-${l.id}`}
                          className="rounded-full text-muted-foreground hover:text-primary"
                        >
                          <Pencil size={15} />
                        </Button>
                        <Button
                          onClick={() => deleteLab(l.id)}
                          variant="ghost"
                          size="icon"
                          data-testid={`delete-lab-${l.id}`}
                          className="rounded-full text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 size={15} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </Layout>
  );
}
